/* FragranceGems — mobile conversion helpers (deferred, ≤ 749px only)
 * 1. Cart page: sticky "Checkout" bar that appears while the real checkout
 *    button is out of view. Proxies the click to the real button so all
 *    theme/app logic (pre-order isolation, discounts, notes) still applies.
 * 2. Menu drawer: turns the flat brand list ("Merken") into a searchable
 *    A–Z list with logo circles and a "Populair" row. Runs during idle time.
 */
(function () {
  'use strict';
  // Re-evaluated on resize/rotate, so narrowing a desktop window (or turning a
  // tablet) activates the mobile layer without a reload.
  var FG_MQ = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;
  if (!FG_MQ) return;

  function initStickyCheckout() {
    if (location.pathname.indexOf('/cart') === -1) return;
    var checkout = document.getElementById('checkout') || document.querySelector('button[name="checkout"]');
    var summary = document.querySelector('.cart-page__summary');
    if (!checkout || !summary || document.querySelector('.fg-sticky-checkout')) return;

    var totalLabelEl = document.querySelector('.cart__total-label');
    var totalValueEl = document.querySelector('.cart__total-value');

    var bar = document.createElement('div');
    bar.className = 'fg-sticky-checkout';
    bar.setAttribute('data-visible', 'false');
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', (checkout.textContent || 'Checkout').trim());
    bar.innerHTML =
      '<div class="fg-sticky-checkout__total">' +
      '<span class="fg-sticky-checkout__label"></span>' +
      '<span class="fg-sticky-checkout__value"></span>' +
      '</div>' +
      '<button type="button" class="fg-sticky-checkout__button"></button>';
    document.body.appendChild(bar);

    var label = bar.querySelector('.fg-sticky-checkout__label');
    var value = bar.querySelector('.fg-sticky-checkout__value');
    var button = bar.querySelector('.fg-sticky-checkout__button');

    function sync() {
      var realBtn = document.getElementById('checkout') || document.querySelector('button[name="checkout"]');
      var blocked = !!document.querySelector('.cart-page__summary.checkout-blocked');
      var empty = !!document.querySelector('.cart-page--empty');
      totalLabelEl = document.querySelector('.cart__total-label');
      totalValueEl = document.querySelector('.cart__total-value');
      label.textContent = totalLabelEl ? totalLabelEl.textContent.trim() : '';
      value.textContent = totalValueEl ? totalValueEl.textContent.trim() : '';
      button.textContent = realBtn ? realBtn.textContent.trim() : 'Checkout';
      var disabled = !realBtn || realBtn.disabled || blocked || empty;
      button.disabled = disabled;
      bar.setAttribute('data-blocked', disabled ? 'true' : 'false');
      if (disabled) show(false);
    }

    function show(on) {
      bar.setAttribute('data-visible', on ? 'true' : 'false');
      document.body.classList.toggle('fg-has-sticky-checkout', !!on);
    }

    button.addEventListener('click', function () {
      var realBtn = document.getElementById('checkout') || document.querySelector('button[name="checkout"]');
      if (!realBtn || realBtn.disabled) return;
      realBtn.click();
    });

    // Show the bar unless the real checkout button is FULLY on screen and not
    // under the sticky header. With a single 0.2 threshold a button cut off by
    // the bottom edge, or scrolled under the header, still counted as "visible",
    // so the sticky bar hid while the real button was not really tappable
    // (measured 2026-09-12: 390px Afrekenen at y 805-857 on an 844px screen;
    // 320px button under the sticky header after scrolling 400px).
    if ('IntersectionObserver' in window) {
      var headerPx = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10) || 64;
      var io = new IntersectionObserver(function (entries) {
        var visible = entries.some(function (e) { return e.isIntersecting && e.intersectionRatio >= 0.95; });
        if (bar.getAttribute('data-blocked') === 'true') return show(false);
        show(!visible);
      }, { threshold: [0, 0.5, 0.95, 1], rootMargin: '-' + headerPx + 'px 0px 0px 0px' });
      var observeReal = function () {
        var realBtn = document.getElementById('checkout') || document.querySelector('button[name="checkout"]');
        if (realBtn) io.observe(realBtn);
      };
      observeReal();
      // The summary is morphed after quantity changes — re-observe & re-sync.
      var mo = new MutationObserver(function () { sync(); io.disconnect(); observeReal(); });
      mo.observe(summary, { childList: true, subtree: true, characterData: true });
    }

    document.addEventListener('cart:update', sync);
    sync();
  }

  /* ----------------------------------------------------------------------
     Brand navigation in the menu drawer.
     Pure DOM decoration — no fetch, no framework, ~1 ms for 41 items. The
     logo SVGs are only painted once the drawer is touched (or the browser
     goes idle), so they never compete with LCP.
     ---------------------------------------------------------------------- */
  var BN_CDN = 'https://cdn.shopify.com/s/files/1/0963/1298/3899/collections/';
  var BN_LOGO = {
    'abdul-samad-al-qurashi': 'Abdul_Samad_Al_Qurashi.svg?v=1791827343',
    'afnan': 'Afnan_88d832b5-f54b-4335-8da1-e3f6e1573ab9.svg?v=1777117831',
    'ahmed-al-maghribi': 'Ahmed_Al_Maghribi_2e6216eb-dc17-486f-8eff-c17dd6cb8a31.svg?v=1777117831',
    'ajmal': 'Ajmal.svg?v=1791821611',
    'al-ambra': 'Al_Ambra.svg?v=1789224744',
    'al-haramain': 'Al_Haramain.svg?v=1789224737',
    'al-majed-oud': 'Al_Majed_Oud.svg?v=1791228557',
    'al-rehab': 'Al_Rehab.svg?v=1791808438',
    'al-wataniah': 'Al_Wataniah_a3d382e1-a52f-4088-9263-1cfe9b6efcbf.svg?v=1777117832',
    'alezz-oud-parfums': 'Alezz_Oud.svg?v=1779190001',
    'amaran': 'Amaran.svg?v=1789224748',
    'amouage': 'Amouage.svg?v=1792797707',
    'anfar': 'Anfar.svg?v=1791808435',
    'arabian-oud': 'Arabian_Oud.svg?v=1791817380',
    'arabiyat-prestige': 'Arabiyat_Prestige_5373dbf8-7ac5-4cba-8dcc-fb8405cf14db.svg?v=1777117832',
    'ard-al-zaafran': 'Ard_Al_Zaafran.svg?v=1791228558',
    'argos': 'Argos.svg?v=1792797726',
    'armaf': 'Armaf_5471be37-6bfa-4b5a-aa82-2154549614ab.svg?v=1777117831',
    'aroma-concepts': 'Aroma_Concepts.svg?v=1789133611',
    'aromatix': 'Aromatix.svg?v=1789224741',
    'asdaaf': 'Asdaaf_0a0e28f9-6ed9-4526-abdc-38fc6ea26821.svg?v=1777117833',
    'assaf': 'Assaf_89e829c5-d183-4446-9d59-06a0fd972de5.svg?v=1777117835',
    'athoor-al-alam': 'Athoor_Al_Alam.svg?v=1791808437',
    'atralia-parfums': 'Atralia.svg?v=1789224860',
    'aurora': 'Aurora.svg?v=1793018294',
    'aurora-scents': 'Aurora_Scents.svg?v=1791288570',
    'bdk-parfums': 'BDK_Parfums.svg?v=1792829203',
    'bujairami': 'Bujairami_b54cef9b-ade7-4b66-ae30-b937156fb575.svg?v=1777117834',
    'byredo': 'Byredo.svg?v=1792797683',
    'caneza': 'Caneza.svg?v=1791295489',
    'creed': 'Creed.svg?v=1792829208',
    'dkhoon-emirates': 'Dkhoon_Emirates.svg?v=1791297205',
    'dua-fragrances': 'Dua_Fragrances.svg?v=1789133613',
    'dumont': 'Dumont_7bc66873-5b72-4d51-9f29-48d458288c72.svg?v=1777117834',
    'emper': 'Emper.svg?v=1791821608',
    'essential-parfums': 'Essential_Parfums.svg?v=1792797688',
    'ex-nihilo': 'Ex_Nihilo.svg?v=1792708169',
    'faan-parfums': 'FAAN.svg?v=1791228556',
    'fascent': 'Fascent.svg?v=1792797740',
    'fragrance-world': 'Fragrance_World.svg?v=1789224739',
    'french-avenue': 'French_Avenue_64b88ca6-600c-4bec-9027-57b43dee126d.svg?v=1777117835',
    'gissah': 'Gissah_844b3724-dd6a-41fb-9b2a-ec6d4747ef77.svg?v=1777117836',
    'goldfield-banks': 'Goldfield_Banks.svg?v=1792797692',
    'gulf-orchid': 'Gulf_Orchid_06b6a973-e9f8-4e17-94d7-0e953bdd6ac8.svg?v=1777117846',
    'heura': 'Heura.svg?v=1791326969',
    'hind-al-oud': 'Hind_Al_Oud.svg?v=1791828013',
    'ibraheem-al-qurashi': 'Ibraq_9989dbf6-730a-476b-a66b-73c1a769f4a1.svg?v=1777117846',
    'initio': 'Initio.svg?v=1792829191',
    'jo-milano-paris': 'Jo_Milano_Paris.svg?v=1789133612',
    'juliette-has-a-gun': 'Juliette_Has_A_Gun.svg?v=1792797698',
    'khadlaj': 'Khadlaj_4bed1c18-9e8e-4f52-8f96-f1af4b360246.svg?v=1777117839',
    'khalis': 'Khalis.svg?v=1791821606',
    'kilian': 'Kilian.svg?v=1792829177',
    'lattafa': 'Lattafa_b613433f-84d9-43d0-8f46-c7b9b86b7f69.svg?v=1777117838',
    'laverne': 'Laverne.svg?v=1791228560',
    'le-bonheur': 'Le_Bonheur.svg?v=1789224747',
    'le-chameau': 'Le_Chameau.svg?v=1791821609',
    'le-falcone': 'Le_Falcone.svg?v=1791295631',
    'les-eaux-primordiales': 'Les_Eaux_Primordiales.svg?v=1792708166',
    'lorenzo-pazzaglia': 'Lorenzo_Pazzaglia.svg?v=1792708167',
    'loui-martin-parfums': 'Loui_Martin.svg?v=1791228561',
    'maison-alhambra': 'Maison_Alhambra.svg?v=1789224740',
    'maison-asrar': 'Maison_Asrar_62b00c88-3721-4c8e-9e4b-21a04a8d0443.svg?v=1777117841',
    'maison-crivelli': 'Maison_Crivelli.svg?v=1792797758',
    'maison-francis-kurkdjian': 'Maison_Francis_Kurkdjian.svg?v=1792797745',
    'maison-margiela': 'Maison_Margiela.svg?v=1792797702',
    'mamlakat-oud': 'Mamlakat_Oud.svg?v=1791808440',
    'mancera': 'Mancera.svg?v=1792829186',
    'matiere-premiere': 'Matiere_Premiere.svg?v=1792797749',
    'mawwal': 'Mawwal.svg?v=1789224750',
    'mind-games': 'Mind_Games.svg?v=1792797754',
    'mykonos': 'Mykonos_ea840af5-2e9a-4526-ac65-1518a25a2d3a.svg?v=1779189999',
    'naseem': 'Naseem.svg?v=1789224743',
    'nasomatto': 'Nasomatto.svg?v=1792797731',
    'niche-emarati': 'Niche_Emarati.svg?v=1791326968',
    'nishane': 'Nishane.svg?v=1792797711',
    'nusuk': 'Nusuk_3e544589-8e36-465b-a70e-2eeafdd3872c.svg?v=1777117845',
    'orientica': 'Orientica.svg?v=1791297206',
    'osma': 'Osma.svg?v=1791295629',
    'parfums-de-marly': 'Parfums_de_Marly.svg?v=1792829196',
    'paris-corner': 'Paris_Corner_e39e94bb-5142-40c8-9a95-fd69621ffc21.svg?v=1777117845',
    'pendora-scents': 'Pendora_Scents.svg?v=1791327951',
    'pernoire': 'Pernoire.svg?v=1792708163',
    'rasasi': 'Rasasi_854155b4-c676-465d-8746-f28059bbbee7.svg?v=1777117843',
    'rayhaan': 'Rayhaan_407acbec-5091-4839-80c1-0548f5ef849f.svg?v=1777117829',
    'reef': 'Reef_f809e8ab-769d-44f5-8734-e44eb02ddaea.svg?v=1777117843',
    'reinvented': 'Reinvented.svg?v=1792708164',
    'riiffs': 'Riiffs_80231292-6627-4e0a-b52f-9ab3f768f273.svg?v=1777117843',
    'sapil': 'Sapil.svg?v=1791326965',
    'sedra': 'Sedra.svg?v=1791808441',
    'shaikh-saeed': 'Shaikh_Saeed.svg?v=1789224746',
    'sospiro': 'Sospiro.svg?v=1792797735',
    'stephane-humbert-lucas': 'Stephane_Humbert_Lucas.svg?v=1792797721',
    'street-origins': 'Street_Origins.svg?v=1791326966',
    'surrati': 'Surrati_2cbf5b51-bcae-4e90-be45-38e0d74b6111.svg?v=1777117843',
    'swiss-arabian': 'Swiss_Arabian_a87e5f15-22d8-446f-b4e4-f813ee2335a4.svg?v=1777117837',
    'tom-ford': 'Tom_Ford.svg?v=1792829181',
    'velixir-parfums': 'Velixir.svg?v=1779190003',
    'volare': 'Volare.svg?v=1779190004',
    'xerjoff': 'Xerjoff.svg?v=1792797716',
    'zimaya': 'Zimaya_13b43687-c628-4191-8dce-daf9e5f62562.svg?v=1777117837'
  };
  var BN_POPULAR = ['bujairami', 'mykonos', 'reef', 'french-avenue', 'assaf', 'rasasi'];
  var BN_TEXT = {
    nl: ['Zoek een merk…', 'Populair', 'Geen merk gevonden'],
    de: ['Marke suchen…', 'Beliebt', 'Keine Marke gefunden'],
    en: ['Search a brand…', 'Popular', 'No brand found'],
    fr: ['Rechercher une marque…', 'Populaire', 'Aucune marque trouvée'],
    es: ['Buscar una marca…', 'Popular', 'No se encontró ninguna marca'],
    it: ['Cerca un marchio…', 'Popolari', 'Nessun marchio trovato'],
    pt: ['Procurar uma marca…', 'Popular', 'Nenhuma marca encontrada'],
    sv: ['Sök ett märke…', 'Populärt', 'Inget märke hittades'],
    da: ['Søg et mærke…', 'Populære', 'Intet mærke fundet'],
    fi: ['Hae merkkiä…', 'Suositut', 'Merkkiä ei löytynyt'],
    el: ['Αναζήτηση μάρκας…', 'Δημοφιλή', 'Δεν βρέθηκε μάρκα'],
    sl: ['Išči znamko…', 'Priljubljeno', 'Nobena znamka ni bila najdena'],
    lt: ['Ieškoti prekės ženklo…', 'Populiaru', 'Prekės ženklas nerastas'],
    pl: ['Szukaj marki…', 'Popularne', 'Nie znaleziono marki']
  };

  function bnFold(s) {
    s = (s || '').toLowerCase();
    // U+0300–U+036F = combining diacritical marks.
    return s.normalize ? s.normalize('NFD').replace(/[̀-ͯ]/g, '') : s;
  }

  // The brand panel is whichever drawer panel holds by far the most collection
  // links. Never keyed on the panel id: that is built from the menu handle and
  // the label differs per locale.
  function bnFindPanel() {
    var panels = document.querySelectorAll('[id^="drawer-panel-"]');
    var panel = null;
    var best = 0;
    for (var i = 0; i < panels.length; i++) {
      var n = panels[i].querySelectorAll('a[href*="/collections/"]').length;
      if (n > best) { best = n; panel = panels[i]; }
    }
    return best < 15 ? null : panel;
  }

  // A morph can drop the injected rows while leaving the marker class or the
  // headers/tools behind, so clear any half-state before rebuilding.
  function bnReset(panel) {
    panel.classList.remove('fg-brandnav');
    panel.classList.remove('fg-live');
    var stale = panel.querySelectorAll('.fg-bn-az, .fg-bn-searchwrap, .fg-bn-pop, .fg-bn-tools, .fg-bn-empty, .fg-bn-chev, .fg-bn-img');
    for (var i = 0; i < stale.length; i++) {
      if (stale[i].parentNode) stale[i].parentNode.removeChild(stale[i]);
    }
    // The filter hides non-matching rows; a rebuilt list must start unfiltered.
    var rows = panel.querySelectorAll('li[hidden]');
    for (var j = 0; j < rows.length; j++) rows[j].hidden = false;
  }

  function initBrandNav() {
    var panel = bnFindPanel();
    // Keyed on the injected rows rather than the marker class: morph strips the
    // class and the rows together, but a partial morph may leave one behind.
    if (!panel || panel.querySelector('.fg-bn-img')) return;
    bnReset(panel);
    var list = panel.querySelector('ul.menu-drawer__menu');
    if (!list) return;

    var lang = (document.documentElement.lang || 'nl').slice(0, 2).toLowerCase();
    var t = BN_TEXT[lang] || BN_TEXT.en;
    var items = [];
    var links = list.querySelectorAll('li > a[href*="/collections/"]');
    if (!links.length) return;

    panel.classList.add('fg-brandnav');

    // 1. decorate every row: logo circle + chevron
    for (var j = 0; j < links.length; j++) {
      var a = links[j];
      var li = a.parentNode;
      var logo = document.createElement('span');
      logo.className = 'fg-bn-img';
      logo.setAttribute('aria-hidden', 'true');
      a.insertBefore(logo, a.firstChild);
      var chev = document.createElement('span');
      chev.className = 'fg-bn-chev';
      chev.setAttribute('aria-hidden', 'true');
      a.appendChild(chev);
      items.push({ li: li, a: a, name: bnFold(a.textContent.trim()) });
    }

    // 2. A–Z headers (the menu is already sorted alphabetically)
    var last = '';
    for (var k = 0; k < items.length; k++) {
      var raw = items[k].a.textContent.trim();
      var letter = raw.charAt(0).toLocaleUpperCase(lang);
      if (letter !== last) {
        var head = document.createElement('li');
        head.className = 'fg-bn-az';
        head.setAttribute('aria-hidden', 'true');
        head.textContent = letter;
        list.insertBefore(head, items[k].li);
        last = letter;
      }
      items[k].head = head;
    }

    // 3. search field + "Populair" row above the list.
    //    Both are inserted as direct children of nav on purpose. A sticky
    //    element can only stay stuck inside its own parent's box, so wrapping
    //    the search field in a short container released it after ~150 px of
    //    scroll -- and because the sticky letter heads are offset by the height
    //    of that field, they then stayed pinned over empty space, covering the
    //    rows. nav spans the whole scrollable content, so the field stays put.
    var searchwrap = document.createElement('div');
    searchwrap.className = 'fg-bn-searchwrap';
    searchwrap.innerHTML =
      '<div class="fg-bn-search">' +
      '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="9" cy="9" r="6" stroke="currentColor" stroke-width="1.6"/><path d="M13.5 13.5 17 17" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>' +
      '<input type="search" class="fg-bn-input" autocomplete="off" autocorrect="off" spellcheck="false" enterkeyhint="search">' +
      '</div>';

    var pop = document.createElement('div');
    pop.className = 'fg-bn-pop';
    var chips = '';
    for (var p = 0; p < BN_POPULAR.length; p++) {
      var hit = list.querySelector('a[href$="/collections/' + BN_POPULAR[p] + '"]');
      if (!hit) continue;
      var txt = hit.querySelector('.menu-drawer__menu-item-text');
      chips += '<a class="fg-bn-chip" href="' + hit.getAttribute('href') + '">' +
        '<span class="fg-bn-img" aria-hidden="true"></span><b>' +
        (txt ? txt.textContent.trim() : '') + '</b></a>';
    }
    if (chips) {
      pop.innerHTML = '<span class="fg-bn-poplabel">' + t[1] + '</span><div class="fg-bn-poprow">' + chips + '</div>';
    }

    var empty = document.createElement('div');
    empty.className = 'fg-bn-empty';
    empty.hidden = true;
    empty.textContent = t[2];

    var nav = list.parentNode;
    nav.insertBefore(searchwrap, list);
    if (chips) nav.insertBefore(pop, list);
    nav.appendChild(empty);

    var input = searchwrap.querySelector('.fg-bn-input');
    input.placeholder = t[0];
    input.setAttribute('aria-label', t[0]);

    // 4. filter
    input.addEventListener('input', function () {
      var q = bnFold(input.value.trim());
      var shown = 0;
      var heads = [];
      for (var x = 0; x < items.length; x++) {
        var on = !q || items[x].name.indexOf(q) !== -1;
        items[x].li.hidden = !on;
        if (on) shown++;
        if (items[x].head) {
          if (heads.indexOf(items[x].head) === -1) { heads.push(items[x].head); items[x].head.hidden = true; }
          if (on) items[x].head.hidden = false;
        }
      }
      if (pop) pop.hidden = !!q;
      empty.hidden = shown !== 0;
    });

    // 5. let the logos download when the browser has a free moment
    var live = function () {
      if (panel.classList.contains('fg-live')) return;
      panel.classList.add('fg-live');
      var imgs = panel.querySelectorAll('.fg-bn-img');
      for (var y = 0; y < imgs.length; y++) {
        var host = imgs[y].parentNode;
        var href = host && host.getAttribute ? host.getAttribute('href') : null;
        var found = href && href.match(/\/collections\/([^/?#]+)/);
        var file = found && BN_LOGO[found[1]];
        if (file) imgs[y].style.backgroundImage = "url('" + BN_CDN + file + "')";
      }
    };
    if (window.requestIdleCallback) window.requestIdleCallback(live, { timeout: 1500 });
    else setTimeout(live, 800);
    document.addEventListener('pointerdown', function (e) {
      if (e.target && e.target.closest && e.target.closest('header-drawer, .menu-drawer-container, .menu-drawer, .header__drawer, [data-header-drawer-type]')) live();
    }, { passive: true, once: true });
  }

  /* The header section is re-rendered through the Section Rendering API shortly
     after load (section-hydration.s.min.js requests ?section_id=…__header_section)
     and morph.s.min.js then syncs that fresh server markup into the live drawer.
     That replaces every li.menu-drawer__list-item, so everything initBrandNav
     injected is thrown away ~0.5 s after it ran and the drawer falls back to the
     plain list. morph's own opt-outs (data-skip-node-update /
     data-skip-subtree-update) only take effect when BOTH the live and the
     incoming node carry the attribute, so they cannot be set from JS — the
     server markup would have to carry them too. Instead: watch the header and
     rebuild whenever the rows are gone. initBrandNav is self-guarding, so this
     is a no-op unless a re-render actually wiped the list. */
  function bnWatch() {
    if (!window.MutationObserver) return;
    var host = document.getElementById('header-group') || document.body;
    if (!host) return;
    var queued = false;
    var mo = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      // Let the whole morph pass settle before rebuilding, and coalesce the
      // mutations our own rebuild triggers into this same tick.
      setTimeout(function () {
        queued = false;
        if (FG_MQ.matches) initBrandNav();
      }, 100);
    });
    mo.observe(host, { childList: true, subtree: true });
  }

  // Both inits are idempotent, so re-running them on a breakpoint change is safe.
  function run() {
    if (!FG_MQ.matches) return;
    initStickyCheckout();
    if (window.requestIdleCallback) window.requestIdleCallback(initBrandNav, { timeout: 2000 });
    else setTimeout(initBrandNav, 300);
  }

  function boot() {
    run();
    bnWatch();
    if (FG_MQ.addEventListener) FG_MQ.addEventListener('change', run);
    else if (FG_MQ.addListener) FG_MQ.addListener(run);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
